/**
 * Inventory Items Service
 *
 * A comprehensive service for managing estate inventory items (assets and liabilities)
 * in probate matters.
 *
 * Features:
 * - Full CRUD operations for inventory items
 * - Category-based organization (real estate, bank accounts, investments, etc.)
 * - Valuation tracking and status management
 * - Institution associations for financial assets
 * - Net worth calculations and reporting
 * - Mock data support for development
 * - React Query hooks for state management
 * - TypeScript support with full type safety
 */

// Types
export * from './types';

// API client helpers (mutations + reads usable from anywhere). Server-side
// reads now flow through `lib/data/matter-reads.ts` (LF-3215).
export {
  fetchInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  setValueConfirmation,
  clearValueConfirmation,
  fetchValueConfirmationSources
} from './client';

// React hooks
export {
  useInventoryItems,
  useInventoryItem,
  useInventoryCurrentView,
  useInventoryItemTypes,
  useCreateInventoryItem,
  useUpdateInventoryItem,
  useDeleteInventoryItem,
  useSetValueConfirmation,
  useClearValueConfirmation,
  useValueConfirmationSources,
  useInventoryService
} from './hooks';
export type { InventoryItemsSummary } from './hooks';

// Utilities
export {
  // Status Priority
  STATUS_PRIORITY,
  determinePrimaryStatus,

  // Formatting
  formatInventoryStatus,
  formatCategoryName,
  getCategoryIcon,
  getCategoryColor,

  // Calculations
  calculateNetWorth,
  calculateTotalAssets,
  calculateTotalLiabilities,

  // Data transformation
  extractValueFromItemInfo,
  generateDescriptionFromItemInfo,
  enrichInventoryItem,

  // Cache keys
  getInventoryItemsCacheKey
} from './utils';
